import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { runsAPI } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Rocket, Code2 } from 'lucide-react';
import { toast } from 'sonner';

const STACKS = [
  { id: 'react', label: 'React', desc: 'Application React avec Vite ou CRA' },
  { id: 'vue', label: 'Vue.js', desc: 'Application Vue 3 avec Vite' },
  { id: 'node', label: 'Node.js', desc: 'API Node.js / Express' },
  { id: 'python', label: 'Python', desc: 'Application Python / FastAPI / Flask' },
  { id: 'laravel', label: 'Laravel', desc: 'Application Laravel PHP' },
];

export default function NewProjectPage() {
  const [goal, setGoal] = useState('');
  const [stack, setStack] = useState('react');
  const [maxSteps, setMaxSteps] = useState(20);
  const [budget, setBudget] = useState(5.0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (goal.trim().length < 10) {
      setError('La description doit contenir au moins 10 caractères');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await runsAPI.create({
        goal: goal.trim(),
        stack,
        max_steps: maxSteps,
        daily_budget_eur: budget,
      });
      toast.success('Projet lancé !');
      navigate(`/projects/${res.data.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de la création';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Nouveau projet</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Décrivez ce que vous souhaitez générer et l'IA s'en charge.
        </p>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Prompt */}
            <div className="space-y-2">
              <Label htmlFor="goal" className="text-sm font-medium">
                Description du projet
              </Label>
              <textarea
                id="goal"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="Ex: Créer une application de gestion de tâches avec authentification, CRUD complet, et interface responsive..."
                rows={5}
                className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                required
                minLength={10}
              />
              <p className="text-xs text-muted-foreground">
                {goal.length}/2000 caractères — Soyez aussi précis que possible
              </p>
            </div>

            {/* Stack */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Stack technique</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {STACKS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStack(s.id)}
                    className={`flex flex-col items-start p-3 rounded-lg border text-left transition-colors ${
                      stack === s.id
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:border-muted-foreground/30 hover:bg-accent/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Code2 className={`w-4 h-4 ${stack === s.id ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="text-sm font-medium">{s.label}</span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">{s.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Options avancées */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-xs text-primary hover:underline"
              >
                {showAdvanced ? 'Masquer' : 'Afficher'} les options avancées
              </button>

              {showAdvanced && (
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Étapes max</Label>
                    <input
                      type="number"
                      value={maxSteps}
                      onChange={(e) => setMaxSteps(parseInt(e.target.value) || 20)}
                      min={1}
                      max={50}
                      className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Budget quotidien (EUR)</Label>
                    <input
                      type="number"
                      value={budget}
                      onChange={(e) => setBudget(parseFloat(e.target.value) || 5.0)}
                      min={0.1}
                      max={100}
                      step={0.1}
                      className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                </div>
              )}
            </div>

            <Button type="submit" className="w-full glow-primary" size="lg" disabled={loading}>
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Rocket className="w-4 h-4 mr-2" />
              )}
              Lancer la génération
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
